import React, { memo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  impactStarVariants,
  impactRingVariants,
  sparkVariants,
  debrisVariants,
} from '../animations/variants';
import { GAME_CONFIG } from '../config/gameConfig';

interface ImpactEffectProps {
  visible: boolean;
  /**
   * Changing this value while visible=true restarts the animation.
   * Use a new string/number for each clash to force a fresh burst.
   */
  triggerKey: string | number;
  /**
   * Visual intensity:
   *   1 — first clash (normal)
   *   2 — second clash (25% bigger, orange tint)
   *   3 — final clash / impact phase (70% bigger, red core, full debris)
   */
  intensity?: 1 | 2 | 3;
}

/** Angle offsets (degrees) for the 8 spark lines radiating from the impact */
const SPARK_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

/**
 * Fixed debris launch angles (degrees) and relative distances.
 * Not random — consistent positions that look spread across all quadrants.
 */
const DEBRIS = [
  { angle: 18,  dist: 88 },
  { angle: 68,  dist: 96 },
  { angle: 118, dist: 80 },
  { angle: 162, dist: 92 },
  { angle: 212, dist: 84 },
  { angle: 258, dist: 98 },
  { angle: 308, dist: 78 },
  { angle: 352, dist: 90 },
];

const INTENSITY_SCALE: Record<1 | 2 | 3, number> = {
  1: 1.0,
  2: 1.28,
  3: 1.72,
};

/**
 * Explosive star-burst collision effect rendered at the center of the arena.
 * Supports three escalating intensity levels, and restarts cleanly each time
 * triggerKey changes (for multi-clash reuse without unmounting the component).
 */
function ImpactEffectComponent({
  visible,
  triggerKey,
  intensity = 1,
}: ImpactEffectProps): React.ReactElement {
  // Unique SVG gradient / filter IDs so concurrent exit+enter don't clash in the DOM
  const idRef = useRef(`ie-${Math.random().toString(36).slice(2, 7)}`);
  const uid = idRef.current;

  const starColor = GAME_CONFIG.colors.impactStar;
  const scale = INTENSITY_SCALE[intensity];

  // Intensity 3: add a red-orange secondary color for "brutal final blow" feel
  const coreColor = intensity === 3 ? '#ff4400' : intensity === 2 ? '#ff8800' : starColor;
  const ringColor = intensity === 3 ? 'rgba(255,80,0,0.7)' : `${starColor}`;

  const starSize = Math.round(160 * scale);
  const starHalf = starSize / 2;
  const sparkLen = Math.round(70 * scale);
  const ringSize = Math.round(80 * scale);
  const ringHalf = ringSize / 2;
  const debrisSize = Math.round(8 * scale);

  const starPath = generateStarPath(starHalf, starHalf, starHalf * 0.94, starHalf * 0.4, 12);

  return (
    <AnimatePresence>
      {visible && (
        <div
          key={triggerKey}
          className="absolute pointer-events-none"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: Math.round(220 * scale),
            height: Math.round(220 * scale),
            zIndex: 30,
          }}
        >
          {/* Outer ring pulse */}
          <motion.div
            className="absolute rounded-full"
            variants={impactRingVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              willChange: 'transform, opacity',
              border: `3px solid ${ringColor}`,
              left: '50%',
              top: '50%',
              width: ringSize,
              height: ringSize,
              marginLeft: -ringHalf,
              marginTop: -ringHalf,
            }}
          />

          {/* Second ring — slightly delayed */}
          <motion.div
            className="absolute rounded-full"
            variants={impactRingVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              willChange: 'transform, opacity',
              border: `2px solid rgba(255,224,0,0.5)`,
              left: '50%',
              top: '50%',
              width: Math.round(50 * scale),
              height: Math.round(50 * scale),
              marginLeft: -Math.round(25 * scale),
              marginTop: -Math.round(25 * scale),
              transitionDelay: '0.05s',
            }}
          />

          {/* Third ring for intensity 3 */}
          {intensity === 3 && (
            <motion.div
              className="absolute rounded-full"
              variants={impactRingVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                willChange: 'transform, opacity',
                border: '2px solid rgba(255,60,0,0.4)',
                left: '50%',
                top: '50%',
                width: Math.round(110 * scale),
                height: Math.round(110 * scale),
                marginLeft: -Math.round(55 * scale),
                marginTop: -Math.round(55 * scale),
                transitionDelay: '0.02s',
              }}
            />
          )}

          {/* Spark rays */}
          {SPARK_ANGLES.map((angle) => (
            <motion.div
              key={angle}
              variants={sparkVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: sparkLen,
                height: Math.round(3 * scale),
                marginTop: -Math.round(1.5 * scale),
                marginLeft: 0,
                transformOrigin: 'left center',
                transform: `rotate(${angle}deg)`,
                background: `linear-gradient(to right, ${starColor}, transparent)`,
                borderRadius: 2,
                willChange: 'transform, opacity',
              }}
            />
          ))}

          {/* Debris chunks — appear at intensity 2+, more visible at 3 */}
          {intensity >= 2 && DEBRIS.map(({ angle, dist }, i) => {
            const rad = (angle * Math.PI) / 180;
            const tx = Math.cos(rad) * dist * scale;
            const ty = Math.sin(rad) * dist * scale;
            return (
              <motion.div
                key={`debris-${i}`}
                variants={debrisVariants}
                initial="hidden"
                animate={{
                  scale: [0, 1, 1],
                  opacity: [1, intensity === 3 ? 0.9 : 0.7, 0],
                  x: [0, tx],
                  y: [0, ty],
                  rotate: [0, angle * 2],
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: debrisSize,
                  height: debrisSize,
                  marginLeft: -debrisSize / 2,
                  marginTop: -debrisSize / 2,
                  background: intensity === 3 ? '#ff6622' : starColor,
                  borderRadius: 2,
                  willChange: 'transform, opacity',
                }}
              />
            );
          })}

          {/* Main star burst */}
          <motion.div
            className="absolute"
            variants={impactStarVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              willChange: 'transform, opacity',
              left: '50%',
              top: '50%',
              width: starSize,
              height: starSize,
              marginLeft: -starHalf,
              marginTop: -starHalf,
            }}
          >
            <svg
              viewBox={`0 0 ${starSize} ${starSize}`}
              className="w-full h-full"
              xmlns="http://www.w3.org/2000/svg"
              style={{ overflow: 'visible' }}
            >
              <defs>
                <radialGradient id={`${uid}-grad`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="white" stopOpacity="1" />
                  <stop offset="40%" stopColor={starColor} stopOpacity="1" />
                  <stop offset="100%" stopColor={coreColor} stopOpacity="0.6" />
                </radialGradient>
                <filter id={`${uid}-glow`}>
                  <feGaussianBlur stdDeviation={intensity === 3 ? 6 : 4} result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* 12-pointed impact star */}
              <path
                d={starPath}
                fill={`url(#${uid}-grad)`}
                filter={`url(#${uid}-glow)`}
              />

              {/* Inner bright core */}
              <circle
                cx={starHalf}
                cy={starHalf}
                r={Math.round(18 * scale)}
                fill="white"
                opacity="0.95"
              />
              <circle
                cx={starHalf}
                cy={starHalf}
                r={Math.round(9 * scale)}
                fill="white"
              />
              {/* Extra hot core for intensity 3 */}
              {intensity === 3 && (
                <circle
                  cx={starHalf}
                  cy={starHalf}
                  r={Math.round(28 * scale)}
                  fill={coreColor}
                  opacity="0.3"
                />
              )}
            </svg>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export const ImpactEffect = memo(ImpactEffectComponent);

// ─── Pre-computed 12-pointed star path ────────────────────────────────────────

function generateStarPath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
): string {
  const segments: string[] = [];
  const total = points * 2;
  for (let i = 0; i < total; i++) {
    const angle = (i * Math.PI) / points - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    segments.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  segments.push('Z');
  return segments.join(' ');
}
